/**
 * BillingSection Component
 *
 * Comprehensive billing management interface for organization owners.
 */

import { useState } from "react";
import { CreditCard, Check, ExternalLink, Loader2, AlertCircle, Calendar } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useOrganizationContext } from "@/providers/OrganizationProvider";
import {
  useBillingStatus,
  usePlans,
  useInvoices,
  useUpcomingInvoice,
  useCreateCheckout,
  useChangeTier,
  usePreviewTierChange,
  useCancelSubscription,
  useReactivateSubscription,
  usePortalSession,
} from "@/hooks/useBilling";
import type { ProrationPreview } from "@/types/billing";

const SUBSCRIPTION_STATUS_LABELS: Record<
  string,
  { label: string; variant: "default" | "secondary" | "destructive" | "outline" }
> = {
  ACTIVE: { label: "Active", variant: "default" },
  PAST_DUE: { label: "Past Due", variant: "destructive" },
  CANCELED: { label: "Canceled", variant: "outline" },
  TRIALING: { label: "Trial", variant: "secondary" },
  UNPAID: { label: "Unpaid", variant: "destructive" },
  NONE: { label: "No Subscription", variant: "outline" },
};

function formatCurrency(amountCents: number | null, currency: string = "usd"): string {
  const amount = (amountCents ?? 0) / 100;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(amount);
}

function formatDate(dateString: string | null): string {
  if (!dateString) return "N/A";
  try {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return dateString;
  }
}

export function BillingSection() {
  const { isOrganizationOwner } = useOrganizationContext();
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [pendingPlanChange, setPendingPlanChange] = useState<string | null>(null);
  const [prorationPreview, setProrationPreview] = useState<ProrationPreview | null>(null);

  const { data: billingStatus, isLoading: statusLoading } = useBillingStatus();
  const { data: plans, isLoading: plansLoading } = usePlans();
  const { data: invoices, isLoading: invoicesLoading } = useInvoices();
  const { data: upcomingInvoice } = useUpcomingInvoice();

  const createCheckout = useCreateCheckout();
  const changeTier = useChangeTier();
  const previewTierChange = usePreviewTierChange();
  const cancelSubscription = useCancelSubscription();
  const reactivateSubscription = useReactivateSubscription();
  const portalSession = usePortalSession();

  const isTierChangePending = changeTier.isPending || changeTier.isPolling;

  const hasActiveSubscription = billingStatus?.provider_subscription_id != null;
  const isSubscriptionCanceled = billingStatus?.subscription_status === "CANCELED";
  const currentPeriodEnd = billingStatus?.current_period_end;
  const canReactivate =
    isSubscriptionCanceled && currentPeriodEnd && new Date(currentPeriodEnd) > new Date();

  const handlePlanSelect = async (planName: string) => {
    if (!isOrganizationOwner) return;

    if (hasActiveSubscription) {
      // Fetch proration preview before showing confirmation dialog
      setSelectedPlan(planName);
      try {
        const preview = await previewTierChange.mutateAsync(planName);
        setProrationPreview(preview);
        setPendingPlanChange(planName);
      } finally {
        setSelectedPlan(null);
      }
    } else {
      // No existing subscription — go straight to Stripe Checkout
      setSelectedPlan(planName);
      try {
        const successUrl = `${window.location.origin}/settings?section=billing&checkout=success`;
        const cancelUrl = `${window.location.origin}/settings?section=billing`;
        await createCheckout.mutateAsync({ tierName: planName, successUrl, cancelUrl });
      } finally {
        setSelectedPlan(null);
      }
    }
  };

  const handleConfirmPlanChange = async () => {
    if (!pendingPlanChange) return;
    await changeTier.mutateAsync(pendingPlanChange);
    setPendingPlanChange(null);
    setProrationPreview(null);
  };

  const handleCancel = async () => {
    await cancelSubscription.mutateAsync();
    setShowCancelDialog(false);
  };

  const handleReactivate = async () => {
    await reactivateSubscription.mutateAsync();
  };

  const handleManagePayment = async () => {
    await portalSession.mutateAsync();
  };

  if (!isOrganizationOwner) {
    return (
      <div className="space-y-6">
        <Card className="rounded-[15px] border-yellow-200 bg-yellow-50 shadow-none dark:bg-yellow-900/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-yellow-600" />
              Owner Only
            </CardTitle>
            <CardDescription>
              Only the organization owner can manage billing and subscriptions.
            </CardDescription>
          </CardHeader>
        </Card>

        {/* Read-only current plan */}
        {statusLoading ? (
          <Skeleton className="h-32 w-full" />
        ) : billingStatus ? (
          <Card className="rounded-[15px] shadow-none">
            <CardHeader>
              <CardTitle>Current plan</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold capitalize">{billingStatus.tier_name}</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Status:{" "}
                    {SUBSCRIPTION_STATUS_LABELS[billingStatus.subscription_status]?.label ||
                      billingStatus.subscription_status}
                  </p>
                </div>
                <Badge {...SUBSCRIPTION_STATUS_LABELS[billingStatus.subscription_status]}>
                  {SUBSCRIPTION_STATUS_LABELS[billingStatus.subscription_status]?.label ||
                    billingStatus.subscription_status}
                </Badge>
              </div>
            </CardContent>
          </Card>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Current Plan Card */}
      <Card className="rounded-[15px] shadow-none">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Current plan
          </CardTitle>
          <CardDescription>Your organization's active subscription</CardDescription>
        </CardHeader>
        <CardContent>
          {statusLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-6 w-32" />
              <Skeleton className="h-4 w-48" />
            </div>
          ) : billingStatus ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold capitalize">{billingStatus.tier_name}</h3>
                  {currentPeriodEnd && (
                    <p className="text-sm text-muted-foreground mt-1">
                      {isSubscriptionCanceled ? "Ends" : "Renews"} on {formatDate(currentPeriodEnd)}
                    </p>
                  )}
                </div>
                <Badge {...SUBSCRIPTION_STATUS_LABELS[billingStatus.subscription_status]}>
                  {SUBSCRIPTION_STATUS_LABELS[billingStatus.subscription_status]?.label}
                </Badge>
              </div>

              {hasActiveSubscription && (
                <>
                  <Separator />
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleManagePayment}
                      disabled={portalSession.isPending}
                    >
                      {portalSession.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Manage Payment Methods
                    </Button>
                    {canReactivate && (
                      <Button
                        variant="default"
                        size="sm"
                        onClick={handleReactivate}
                        disabled={reactivateSubscription.isPending}
                      >
                        {reactivateSubscription.isPending && (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        )}
                        Reactivate Subscription
                      </Button>
                    )}
                  </div>
                </>
              )}
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/* Available Plans */}
      <Card className="rounded-[15px] shadow-none">
        <CardHeader>
          <CardTitle>Available Plans</CardTitle>
          <CardDescription>Choose the plan that fits your needs</CardDescription>
        </CardHeader>
        <CardContent>
          {plansLoading ? (
            <div className="grid gap-4 md:grid-cols-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-48 w-full" />
              ))}
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-3">
              {[...(plans ?? [])]
                .sort((a, b) => (a.price_amount ?? 0) - (b.price_amount ?? 0))
                .map((plan) => (
                  <Card
                    key={plan.name}
                    className={plan.is_current ? "border-primary shadow-md" : ""}
                  >
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center justify-between text-base">
                        <span className="capitalize">{plan.name}</span>
                        {plan.is_current && (
                          <Badge variant="default">
                            <Check className="h-3 w-3 mr-1" />
                            Current
                          </Badge>
                        )}
                      </CardTitle>
                      <div className="flex items-baseline gap-1 mt-1">
                        <span className="text-3xl font-semibold tracking-tight">
                          {formatCurrency(plan.price_amount)}
                        </span>
                        {plan.price_interval ? (
                          <span className="text-sm text-muted-foreground">
                            /{plan.price_interval.toLowerCase()}
                          </span>
                        ) : (
                          <span className="text-sm text-muted-foreground">/month</span>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 mb-4">
                        <p className="text-sm text-muted-foreground">
                          {plan.max_reports === null
                            ? "Unlimited reports"
                            : `${plan.max_reports} reports`}
                        </p>
                      </div>
                      {!plan.is_current && plan.name !== "free" && (
                        <Button
                          className="w-full"
                          onClick={() => handlePlanSelect(plan.name)}
                          disabled={
                            selectedPlan === plan.name ||
                            createCheckout.isPending ||
                            isTierChangePending
                          }
                        >
                          {selectedPlan === plan.name && (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          )}
                          {hasActiveSubscription ? "Switch to this plan" : "Subscribe"}
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upcoming Invoice */}
      {upcomingInvoice && (
        <Card className="rounded-[15px] shadow-none">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Upcoming Invoice
            </CardTitle>
            <CardDescription>Preview of your next charge</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">
                  {formatCurrency(upcomingInvoice.amount_due, upcomingInvoice.currency)}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Due on {formatDate(upcomingInvoice.period_end)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Invoice History — always shown if there are any invoices */}
      {(invoicesLoading || (invoices && invoices.length > 0)) && (
        <Card className="rounded-[15px] shadow-none">
          <CardHeader>
            <CardTitle>Invoice history</CardTitle>
            <CardDescription>Past invoices and payment records</CardDescription>
          </CardHeader>
          <CardContent>
            {invoicesLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : invoices && invoices.length > 0 ? (
              <div className="space-y-3">
                {billingStatus?.subscription_status === "PAST_DUE" && (
                  <div className="flex items-start gap-2 rounded-[10px] border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                    <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>
                      Your account has an outstanding payment. Please pay the open invoice below to
                      avoid service interruption.
                    </span>
                  </div>
                )}
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoices.map((invoice) => (
                      <TableRow key={invoice.id}>
                        <TableCell>{formatDate(invoice.period_end)}</TableCell>
                        <TableCell>
                          {formatCurrency(invoice.amount_due, invoice.currency)}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              invoice.status === "paid"
                                ? "default"
                                : invoice.status === "open"
                                  ? "destructive"
                                  : "outline"
                            }
                          >
                            {invoice.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {invoice.hosted_invoice_url && invoice.status !== "uncollectible" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                window.open(
                                  invoice.hosted_invoice_url!,
                                  "_blank",
                                  "noopener,noreferrer"
                                )
                              }
                            >
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : null}
          </CardContent>
        </Card>
      )}

      {/* Subscription Management */}
      {hasActiveSubscription && !isSubscriptionCanceled && (
        <Card className="rounded-[15px] shadow-none">
          <CardHeader>
            <CardTitle>Subscription management</CardTitle>
            <CardDescription>Manage your active subscription</CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              variant="destructive"
              onClick={() => setShowCancelDialog(true)}
              disabled={cancelSubscription.isPending}
            >
              {cancelSubscription.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Cancel Subscription
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Plan Change Confirmation Dialog */}
      <AlertDialog
        open={!!pendingPlanChange}
        onOpenChange={(open) => {
          if (!open) {
            setPendingPlanChange(null);
            setProrationPreview(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Plan Change</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  You're switching to the{" "}
                  <span className="font-semibold capitalize">{pendingPlanChange}</span> plan.
                </p>
                {prorationPreview &&
                  (() => {
                    const {
                      immediate_charge,
                      credits_used,
                      account_credit,
                      next_invoice_amount,
                      currency,
                    } = prorationPreview;
                    const nextAfterCredit = Math.max(0, next_invoice_amount - account_credit);
                    const monthsOfCredit =
                      next_invoice_amount > 0
                        ? Math.floor(account_credit / next_invoice_amount)
                        : 0;
                    const grossCharge = immediate_charge + credits_used;

                    // Case 1: upgrading and existing credit was applied against the proration
                    const isUsingCredit = credits_used > 0;
                    // Case 2: downgrading — no charge, credit generated on account
                    const isDowngrade =
                      immediate_charge === 0 && credits_used === 0 && account_credit > 0;

                    return (
                      <div className="space-y-2 rounded-[10px] border p-3 text-sm">
                        {isUsingCredit ? (
                          <>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Amount owed</span>
                              <span className="font-semibold">
                                {formatCurrency(grossCharge, currency)}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Credits used</span>
                              <span className="font-semibold text-green-600">
                                −{formatCurrency(credits_used, currency)}
                              </span>
                            </div>
                            <div className="flex justify-between border-t pt-2">
                              <span className="text-muted-foreground">Charged now</span>
                              <span className="font-semibold">
                                {formatCurrency(immediate_charge, currency)}
                              </span>
                            </div>
                            {account_credit > 0 && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Credits remaining</span>
                                <span className="font-semibold text-green-600">
                                  +{formatCurrency(account_credit, currency)}
                                </span>
                              </div>
                            )}
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Next billing cycle</span>
                              <span className="font-semibold">
                                {nextAfterCredit === 0 ? (
                                  <span className="text-green-600">Free (credit applied)</span>
                                ) : (
                                  formatCurrency(nextAfterCredit, currency)
                                )}
                              </span>
                            </div>
                          </>
                        ) : isDowngrade ? (
                          <>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Account credit</span>
                              <span className="font-semibold text-green-600">
                                +{formatCurrency(account_credit, currency)}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Next billing cycle</span>
                              <span className="font-semibold">
                                {nextAfterCredit === 0 ? (
                                  <span className="text-green-600">Free (credit applied)</span>
                                ) : (
                                  formatCurrency(nextAfterCredit, currency)
                                )}
                              </span>
                            </div>
                            {monthsOfCredit > 1 && (
                              <p className="text-xs text-muted-foreground">
                                Your credit covers approximately {monthsOfCredit} months of the new
                                plan.
                              </p>
                            )}
                          </>
                        ) : (
                          <>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Charged now</span>
                              <span className="font-semibold">
                                {formatCurrency(immediate_charge, currency)}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Next billing cycle</span>
                              <span className="font-semibold">
                                {formatCurrency(next_invoice_amount, currency)}
                              </span>
                            </div>
                          </>
                        )}
                        <p className="text-xs text-muted-foreground">
                          Amounts are estimates and may differ by a few cents due to per-second
                          proration timing.
                        </p>
                      </div>
                    );
                  })()}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmPlanChange} disabled={isTierChangePending}>
              {isTierChangePending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Subscription?</AlertDialogTitle>
            <AlertDialogDescription>
              Your subscription will remain active until the end of your current billing period (
              {formatDate(currentPeriodEnd ?? null)}). After that, you'll be downgraded to the Free
              plan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Subscription</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancel}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Yes, Cancel
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
